import { defineComponent as _defineComponent } from 'vue'
import TtDownlineSummary from "@/components/modules/bet-details/TtDownlineSummary.vue";
import TTPageDetailsModal from "@/components/modules/bet-details/TTPageDetailsModal.vue";
import RenameTTPageModal from "@/components/modules/bet-details/RenameTTPageModal.vue";
import TTDeletePageModal from "@/components/modules/bet-details/TTDeletePageModal.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import { StoredPageType, TtPage } from "@/CodegenTypes";
import {
  buildTtPageDetailsRows,
  TtPageDetailsRow,
  TtPageDetailsUser,
} from "./ttPageDetailsRows";

import { sumBy, startCase, first } from "lodash";

import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import { customMoment, vueApp } from "@/main";
import { FEATURE_TOGGLES } from "@/config";
import { useTranslation } from "@/store/translation";
import { computed, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'TTPageDetails',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const selectedPage = ref<null | TtPageDetailsRow>(null);
const showPageDetailModal = ref(false);
const showRenamePageModal = ref(false);
const showDeletePageModal = ref(false);
const showAllPageDetails = ref(false);
const showAllPageDetailsScopeKey = ref<string | null>(null);

const selectedStartReleaseDate = computed(() =>
  getRouteQueryValue(route.query.startReleaseDate)
);
const selectedUserId = computed(() => getRouteQueryValue(route.query.userId));
const pageDetailsScopeKey = computed(
  () => `${selectedUserId.value || ""}:${selectedStartReleaseDate.value || ""}`
);
const includeAllPageDetails = computed(
  () =>
    showAllPageDetails.value &&
    showAllPageDetailsScopeKey.value === pageDetailsScopeKey.value
);
const userTtPagesSummaryVariables = computed(() => {
  const releaseDate = customMoment(selectedStartReleaseDate.value)
    .startOf("day")
    .toDate();
  return {
    releaseDateStart: releaseDate,
    releaseDateEnd: releaseDate,
    behalfOf: selectedUserId.value,
    includeDownlines: includeAllPageDetails.value,
  };
});

const {
  result: userTtPagesSummaryResult,
  loading: apolloLoading,
  refetch,
} = useQuery<{ user: TtPageDetailsUser }>(
  queries.userTtPagesSummary,
  userTtPagesSummaryVariables,
  {
    enabled: computed(() => !!selectedStartReleaseDate.value),
    fetchPolicy: "cache-and-network",
  }
);

const user = computed(() => userTtPagesSummaryResult.value?.user ?? null);
const pageDetailsData = computed(() =>
  buildTtPageDetailsRows(user.value, {
    includeDownlines: includeAllPageDetails.value,
  })
);
const pageDetailTotal = computed(() => {
  const data = pageDetailsData.value;
  return {
    amount: formatDollar(sumBy(data, (d) => d.amount)),
    // rebate: formatDollar(sumBy(data, d => d.rebate)),
    // payable: formatDollar(sumBy(data, d => d.payable))
  };
});

watch([selectedUserId, selectedStartReleaseDate], resetPageDetailsScope);

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? first(query) : query;
}

function rowTestId(value: string) {
  return String(value || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function loadAllDownlinePageDetails() {
  showAllPageDetails.value = true;
  showAllPageDetailsScopeKey.value = pageDetailsScopeKey.value;
  closePageModals();
}

function resetPageDetailsScope() {
  showAllPageDetails.value = false;
  showAllPageDetailsScopeKey.value = null;
  closePageModals();
}

function closePageModals() {
  selectedPage.value = null;
  showPageDetailModal.value = false;
  showRenamePageModal.value = false;
  showDeletePageModal.value = false;
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD/MM/YYYY hh:mm:ss A");
}

async function savePage(id: TtPage["id"]) {
  let page: TtPage | undefined;
  if (FEATURE_TOGGLES.useV1Legacy) {
    page = pageDetailsData.value.find((p) => p.id === id) as
      | TtPage
      | undefined;
  } else {
    const result = await apolloClient.query<{ ttPage: TtPage }>({
      query: queries.ttPage,
      variables: {
        id,
      },
    });
    page = result.data.ttPage;
  }

  if (!page) {
    return;
  }
  const pageToSave = page;

  vueApp.$confirm.require({
    header: "Save bet?",
    message: "Bet page will be saved under Saved Bets",
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        const result = await apolloClient.mutate({
          mutation: mutations.saveTtEntry,
          variables: {
            page: {
              id: undefined,
              page: pageToSave.name,
              userId: pageToSave.userId,
              entries: pageToSave.entries.map((e) => {
                const entry = e as {
                  numbers?: string[];
                  numbersText?: string;
                  unit: number;
                  mode?: string;
                };
                const numbers =
                  Array.isArray(entry.numbers) && entry.numbers.length
                    ? entry.numbers
                    : entry.numbersText?.split("-") ?? [];
                return {
                  number1: numbers[0] || "",
                  number2: numbers[1] || "",
                  number3: numbers[2] || "",
                  number4: numbers[3] || "",
                  number5: numbers[4] || "",
                  number6: numbers[5] || "",
                  number7: numbers[6] || "",
                  number8: numbers[7] || "",
                  number9: numbers[8] || "",
                  number10: numbers[9] || "",
                  day: 4,
                  unit: entry.unit,
                  mode: entry.mode,
                };
              }),
              storedPageType: StoredPageType.Saved,
            },
          },
        });
        const pageId = result.data.saveTtEntry;

        vueApp.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Page saved to Saved Bets",
          life: 3000,
        });

        router.push({
          name: "tt saved bet",
          query: { pageId, userId: pageToSave.userId },
        });
      } catch (err) {
        errorMutationNotification(err);
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });

  return id;
}

function deletePage(id: TtPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showDeletePageModal.value = true;
}

function renamePage(id: TtPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showRenamePageModal.value = true;
}

function reloadPageName() {
  refetch(userTtPagesSummaryVariables.value);
}

function reloadAll() {
  refetch(userTtPagesSummaryVariables.value);
}

function viewPageDetails(id: TtPage["id"]) {
  const page = pageDetailsData.value.find((p) => p.id === id);
  if (!page) {
    return;
  }
  selectedPage.value = page;
  showPageDetailModal.value = true;
}

return { __sfc: true,route, router, $t, t, selectedPage, showPageDetailModal, showRenamePageModal, showDeletePageModal, showAllPageDetails, showAllPageDetailsScopeKey, selectedStartReleaseDate, selectedUserId, pageDetailsScopeKey, includeAllPageDetails, userTtPagesSummaryVariables, userTtPagesSummaryResult, apolloLoading, refetch, user, pageDetailsData, pageDetailTotal, getRouteQueryValue, rowTestId, loadAllDownlinePageDetails, resetPageDetailsScope, closePageModals, formatDate, savePage, deletePage, renamePage, reloadPageName, reloadAll, viewPageDetails, TtDownlineSummary, TTPageDetailsModal, RenameTTPageModal, TTDeletePageModal, startCase, formatDollar, FEATURE_TOGGLES }
}

})